// Row selection for the Tabellenansichten (Mitarbeiter, Fuhrpark). Forked from
// report_center_bulk_select_controller.js, with the bulk-edit dialog wired in: openEditor writes
// the effective count into the dialog before showing it, so the preview line can never claim a
// different number than the request will touch.
//
// Deliberately NOT named employee-bulk-select: that identifier already belongs to the SlimSelect
// helper on the sick-note and leave-request forms.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "selectedCount",
    "normalHeader",
    "selectionHeader",
    "selectAllFlag",
    "selectAllBanner",
    "selectAllBannerText",
    "editor",
    "editorCount",
    "explicitOnly",
  ]

  static values = {
    total: Number, // total matches across all pages, not just this one
    pageSize: Number,
  }

  connect() {
    this.lastChecked = null
    this.update()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.checkboxTargets.forEach((cb) => {
      cb.checked = checked
    })
    this.resetSelectAllFlag()
    this.update()
  }

  toggle(event) {
    if (event.shiftKey && this.lastChecked) {
      const checkboxes = this.checkboxTargets
      const start = checkboxes.indexOf(this.lastChecked)
      const end = checkboxes.indexOf(event.target)
      const range = [start, end].sort((a, b) => a - b)

      for (let i = range[0]; i <= range[1]; i++) {
        checkboxes[i].checked = event.target.checked
      }
    }

    this.lastChecked = event.target
    this.resetSelectAllFlag()
    this.update()
  }

  // Hand the whole filtered result set to the server. The checkboxes stay checked so the count
  // still reads sensibly, but select_all=1 is what the controller acts on.
  selectAllMatching() {
    if (this.hasSelectAllFlagTarget) {
      this.selectAllFlagTarget.value = "1"
    }
    this.checkboxTargets.forEach((cb) => {
      cb.checked = true
    })
    this.update()
  }

  clear() {
    this.checkboxTargets.forEach((cb) => {
      cb.checked = false
    })
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.resetSelectAllFlag()
    this.update()
  }

  // A second bulk action names its own <dialog> through data-table-bulk-select-dialog-param;
  // without it the Sammelbearbeitung editor opens, as before.
  openEditor(event) {
    const dialogId = event?.params?.dialog
    const dialog = dialogId
      ? document.getElementById(dialogId)
      : this.hasEditorTarget && this.editorTarget
    if (!dialog) return

    this.editorCountTargets.forEach((el) => {
      el.textContent = this.effectiveCount
    })
    dialog.showModal()
  }

  closeEditor(event) {
    const dialog = event?.target?.closest?.("dialog") || (this.hasEditorTarget && this.editorTarget)
    if (dialog) dialog.close()
  }

  resetSelectAllFlag() {
    if (this.hasSelectAllFlagTarget) {
      this.selectAllFlagTarget.value = "0"
    }
  }

  get selectedCount() {
    return this.checkboxTargets.filter((cb) => cb.checked).length
  }

  get selectAllActive() {
    return this.hasSelectAllFlagTarget && this.selectAllFlagTarget.value === "1"
  }

  get effectiveCount() {
    return this.selectAllActive ? this.totalValue : this.selectedCount
  }

  update() {
    this.updateSelectAllState()
    this.updateHeaderVisibility()
    this.updateSelectAllBanner()
  }

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const total = this.checkboxTargets.length
    const checked = this.selectedCount

    this.selectAllTarget.checked = checked === total && total > 0
    this.selectAllTarget.indeterminate = checked > 0 && checked < total
  }

  updateHeaderVisibility() {
    const hasSelection = this.selectedCount > 0

    if (this.hasNormalHeaderTarget && this.hasSelectionHeaderTarget) {
      this.normalHeaderTarget.classList.toggle("hidden", hasSelection)
      this.normalHeaderTarget.classList.toggle("flex", !hasSelection)
      this.selectionHeaderTarget.classList.toggle("hidden", !hasSelection)
      this.selectionHeaderTarget.classList.toggle("flex", hasSelection)
    }

    this.selectedCountTargets.forEach((el) => {
      el.textContent = this.effectiveCount
    })

    // Actions the server only accepts on ticked rows (the bulk delete) step aside while
    // "alle Treffer" is active, rather than opening a dialog that promises the whole result set.
    this.explicitOnlyTargets.forEach((el) => {
      el.classList.toggle("hidden", this.selectAllActive)
    })
  }

  // Offer "alle Treffer" only when the page really is a subset of the result set and the whole
  // page is already checked — otherwise the banner is noise.
  updateSelectAllBanner() {
    if (!this.hasSelectAllBannerTarget) return

    const pageSize = this.checkboxTargets.length
    const wholePageChecked = pageSize > 0 && this.selectedCount === pageSize
    const moreThanOnePage = this.totalValue > pageSize
    const show = wholePageChecked && moreThanOnePage && !this.selectAllActive

    this.selectAllBannerTarget.classList.toggle("hidden", !show)
    this.selectAllBannerTarget.classList.toggle("block", show)

    if (show && this.hasSelectAllBannerTextTarget) {
      const template = this.selectAllBannerTextTarget.dataset.template
      this.selectAllBannerTextTarget.textContent = template
        ? template.replace("%{count}", pageSize)
        : ""
    }
  }
}
